import React, { useMemo, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';

const getSearchValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '';
};

export default function AppDatatable({
  title,
  subtitle = '',
  headerActions = null,
  headerContent = null,
  data,
  loading = false,
  noFoundMessage = 'No records found.',
  hideSearch = false,
  tableMinWidth,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const columns = useMemo(() => data?.columns || [], [data?.columns]);
  const rows = useMemo(() => data?.rows || [], [data?.rows]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      columns.some((column) => getSearchValue(row?.[column.field]).toLowerCase().includes(term))
    );
  }, [columns, rows, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;

    const column = columns.find((item) => item.field === sortField);
    if (!column) return filteredRows;

    const getValue = (row) => (
      typeof column.getSortValue === 'function'
        ? column.getSortValue(row)
        : row?.[sortField]
    );

    return [...filteredRows].sort((left, right) => {
      const leftValue = getValue(left);
      const rightValue = getValue(right);
      const leftEmpty = leftValue === null || leftValue === undefined || leftValue === '';
      const rightEmpty = rightValue === null || rightValue === undefined || rightValue === '';

      if (leftEmpty || rightEmpty) {
        if (leftEmpty && rightEmpty) return 0;
        return leftEmpty ? 1 : -1;
      }

      const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [columns, filteredRows, sortDirection, sortField]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleSort = (field) => {
    setSortDirection((currentDirection) => (
      sortField === field && currentDirection === 'asc' ? 'desc' : 'asc'
    ));
    setSortField(field);
    setPage(0);
  };

  const handleChangePage = (_, nextPage) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      {title || headerActions || headerContent ? (
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography
                variant="h5"
                color="text.primary"
                sx={{ fontWeight: 700, letterSpacing: 0, mb: subtitle ? 0.75 : 0 }}
              >
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography sx={{ color: 'text.secondary', maxWidth: 920, lineHeight: 1.5 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>

          {headerActions ? (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: '#f8fafc',
                border: '1px solid rgba(148, 163, 184, 0.24)',
                display: 'flex',
                justifyContent: { xs: 'stretch', lg: 'flex-end' },
                alignItems: 'center',
              }}
            >
              {headerActions}
            </Box>
          ) : null}
          {headerContent ? (
            <Box sx={{ mt: 2 }}>
              {headerContent}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {!hideSearch ? (
        <TextField
          size="small"
          label="Search"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setPage(0);
          }}
          sx={{ mb: 2, maxWidth: 320 }}
        />
      ) : null}

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <TableContainer>
        <Table size="small" sx={{ minWidth: tableMinWidth }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field || column.label} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={sortField === column.field ? sortDirection : 'asc'}
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>Fetching records...</Box>
                </TableCell>
              </TableRow>
            ) : pagedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{noFoundMessage}</Box>
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((row, rowIndex) => (
                <TableRow key={row._id || row.id || `row-${rowIndex}`} hover sx={row._rowSx || undefined}>
                  {columns.map((column) => (
                    <TableCell key={`${column.field || column.label}-${rowIndex}`} sx={{ verticalAlign: 'top' }}>
                      {row?.[column.field] ?? '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
}
